import { useAuth } from "./AuthProvider";
import LoginScreen from "./LoginScreen";
import App from "./App";
import PublicRoomDisplay from "./PublicRoomDisplay";

// Default theme used before app loads (login screen)
const DEFAULT_THEME = {
  primary: "#6B5840", primaryLight: "#F0EBE0", accent: "#C4A962",
  sidebar: "#1A1613", sidebarEnd: "#262018", sidebarText: "#F5EDE0", sidebarMuted: "#A09484",
  bg: "#F3EEE4", surface: "#FDFBF7", surfaceAlt: "#FAF6EE", border: "#E8E0D4",
  text: "#1A1613", textMuted: "#8B7E6A", textSub: "#4A3F33", borderInput: "#D4CBBB",
};

export default function AppShell() {
  const { user, loading } = useAuth();

  // Public TV board: ?display=rooms renders full-screen with no login required.
  // Checked after the hook above so hook order stays stable.
  const isRoomDisplay = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("display") === "rooms";
  if (isRoomDisplay) {
    return <PublicRoomDisplay />;
  }

  // Global styles needed on every screen
  const globalCSS = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; }
    @keyframes slideIn { from { opacity:0; transform:translateY(-10px) } to { opacity:1; transform:translateY(0) } }
    @keyframes fadeUp { from { opacity:0; transform:translateY(15px) } to { opacity:1; transform:translateY(0) } }
    input:focus, select:focus, textarea:focus { border-color: #B8A88A !important; box-shadow: 0 0 0 3px rgba(184,168,138,.15) !important; }
    button:hover { opacity: .92; }
    button:active { transform: scale(.98); }
    ::-webkit-scrollbar { width: 5px; }
    ::-webkit-scrollbar-thumb { background: #C4B89A; border-radius: 3px; }
  `;

  // Loading state — Firebase is checking if user has a session
  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(145deg, ${DEFAULT_THEME.sidebar}, ${DEFAULT_THEME.sidebarEnd})`,
          fontFamily: "'DM Sans', sans-serif",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <style>{globalCSS}</style>
        <div
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            border: `3px solid ${DEFAULT_THEME.sidebarEnd}`,
            borderTopColor: DEFAULT_THEME.accent,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ color: DEFAULT_THEME.accent, fontSize: 16 }}>Signing you in…</div>
        <div style={{ color: DEFAULT_THEME.sidebarMuted, fontSize: 13 }}>Please wait while we load your account.</div>
      </div>
    );
  }

  // Not signed in, or signed in but no profile — show login screen
  if (!user) {
    return (
      <>
        <style>{globalCSS}</style>
        <LoginScreen theme={DEFAULT_THEME} />
      </>
    );
  }

  // Signed in with a valid profile — show the app
  return (
    <>
      <style>{globalCSS}</style>
      <App />
    </>
  );
}
